import {
  Button,
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@sea-snag/ui';
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from 'recharts';

import { formatCents, type IncomeMetric, type MonthPoint } from '../../lib/income';

export const METRICS: Array<{ value: IncomeMetric; label: string }> = [
  { value: 'gross', label: 'Gross revenue' },
  { value: 'net', label: 'Net revenue' },
  { value: 'owner', label: 'Owner share' },
  { value: 'company', label: 'Charter company share' },
];

const chartConfig = {
  amount: { label: 'Amount', color: 'var(--chart-1)' },
} satisfies ChartConfig;

interface ChartPoint {
  label: string;
  amount: number;
  cents: number;
  incomplete: boolean;
}

/** The metric picker shared by the Overview and Year comparison charts. */
export function MetricPicker({
  metric,
  onChange,
}: {
  metric: IncomeMetric;
  onChange: (m: IncomeMetric) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1" role="group" aria-label="Metric">
      {METRICS.map((m) => (
        <Button
          key={m.value}
          variant={metric === m.value ? 'default' : 'outline'}
          size="sm"
          className="h-7 px-2 text-xs"
          aria-pressed={metric === m.value}
          onClick={() => onChange(m.value)}
        >
          {m.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * One bar per month charters ended in, for the chosen metric (#217). A month
 * whose split is partly unknown is faded and says so: it is never drawn as a
 * smaller figure that could pass for a real one.
 */
export function IncomeMonthlyChart({
  series,
  metric,
  onMetricChange,
  currency,
}: {
  series: MonthPoint[];
  metric: IncomeMetric;
  onMetricChange: (m: IncomeMetric) => void;
  currency: string;
}) {
  const data: ChartPoint[] = series.map((p) => ({
    label: p.label,
    amount: p.cents / 100,
    cents: p.cents,
    incomplete: p.incomplete,
  }));
  const compact = new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-3">
      <MetricPicker metric={metric} onChange={onMetricChange} />
      <ChartContainer config={chartConfig} className="h-[260px] w-full">
        <BarChart
          data={data}
          accessibilityLayer
          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
        >
          <CartesianGrid vertical={false} />
          <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} fontSize={11} />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={56}
            tickFormatter={(v: number) => compact.format(v)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(_value, _name, item) => {
                  const p = item.payload as ChartPoint;
                  return (
                    <span className="tabular-nums">
                      {formatCents(p.cents, currency, 2)}
                      {p.incomplete && (
                        <span className="ml-1 text-muted-foreground">(incomplete)</span>
                      )}
                    </span>
                  );
                }}
              />
            }
          />
          <Bar dataKey="amount" fill="var(--color-amount)" radius={[3, 3, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.label} fillOpacity={d.incomplete ? 0.35 : 1} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
      {data.some((d) => d.incomplete) && (
        <p className="text-xs text-muted-foreground">
          Faded months are incomplete: some of their boats have no owner share or ownership type for
          this season yet, so only the part that can be split is shown.
        </p>
      )}
    </div>
  );
}
